import { formatDate } from '@angular/common';
import { Component, effect, EffectRef, inject, Injector, linkedSignal, OnInit, Signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AddUnloadingDialogComponent } from '../add-unloading-dialog/add-unloading-dialog.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { ErrorWarningDialogComponent } from '../error-warning-dialog/error-warning-dialog.component';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { FishingEvent } from '../shared/fishing-event.model';
import { HMSDelivery } from '../shared/hms-delivery.model';
import { Fishery, LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { SetErrorBlock } from '../shared/set-error-block.model';
import { TripValidator, arrivalAfterDepartureValidator, lookupItemValidator, vesselDataValidator } from '../shared/trip-validator.model';
import { Unloading } from '../shared/unloading.model';
import { SubmitDialogComponent } from '../submit-dialog/submit-dialog.component';
import { VesselData } from '../vessel-reg/vessel-data.model';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { SetSelectionDialogComponent } from '../set-selection-dialog/set-selection-dialog.component';
import { Insights } from '../shared/insights.service';

enum DeliveryMode { Current, Completed, EditCompleted, Draft };

let SCREEN_WIDTH: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';

@Component({
    selector: 'app-hms-delivery-detail',
    templateUrl: './hms-delivery-detail.component.html',
    styleUrl: './hms-delivery-detail.component.css',
    standalone: false
})
export class HmsDeliveryDetailComponent implements OnInit {
  private injector = inject(Injector);
  updateEffect!: EffectRef;
  /**
  * Determines the view which can be: 0 = The current unsubmitted delivery, 1 = A completed delivery, 2 = A completed delivery being edited
  */
  viewMode: DeliveryMode = DeliveryMode.Current;

  /**
   * If viewing a completed delivery, index into {@link LocalStorageService.received_landings}
   */
  completedInd: number = 0;

  /**
   * When viewing a completed delivery, it can be edited and resubmitted within 7 days of its original submission date
   */
  canEdit: boolean = false;

  /**
   * The current landing/delivery
   */
  landing: HMSDelivery = new HMSDelivery();
  unloadingsColumns: string[] = ['unloadingPort', 'unloadingDate', 'species'];
  @ViewChild(MatTable) table!: MatTable<Unloading>;

  previousSetIndex: number = 0;
  matToolTipText: string = "Back to Home"

  /**
   * Use a separate control for permit number
   */
  landingForm: FormGroup = new FormGroup({
    vessel: new FormControl<VesselData>(new VesselData('', ''), [Validators.required, vesselDataValidator()]), //Default to last vessel entered
    operator: new FormControl<string>('', [Validators.required]), //Default to account name
    departureDate: new FormControl('', [Validators.required]), //Default to earliest set date
    departurePort: new FormControl<LookupItem>(new LookupItem(''), [Validators.required, lookupItemValidator()]),
    arrivalPort: new FormControl<LookupItem>(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last port entered
    arrivalDate: new FormControl('', [Validators.required]),
  }, { validators: arrivalAfterDepartureValidator() });

  /**
   * For populating the vessel dropdown
   */
  vesselOptions: WritableSignal<VesselData[]>;

  /**
   * On every change to the landing form, this subscription catches it and runs the form update procedure
   */
  landingFormSubscription: Subscription = new Subscription();

  portOptions: LookupItem[] = [];
  filteredDeparturePorts!: Observable<LookupItem[]>;
  filteredArrivalPorts!: Observable<LookupItem[]>;
  portSubscription!: Subscription;

  lastBuyer: string = '';

  /**
   * Periodically updated from {@link TripValidator.errorWarningDialogContent}
   */
  errors: SetErrorBlock[] = [];
  onlyWarnings: boolean = false;
  /**
   * Which attached sets have errors?
   */
  validSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * When editing a completed delivery, we use this to keep track of which sets were originally submitted with the delivery
   */
  preExistingSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * The sets on this delivery, sorted
   */
  sortedSets: FishingEvent[] = [];

  landingSubscription!: Subscription;

  previousScreen: string = 'menu';

  minArrivalDate: Date = new Date();


  constructor(
    public dataSync: LocalStorageService,
    public tripValidator: TripValidator,
    public router: Router,
    private route: ActivatedRoute,
    private onlineStatusService: OnlineStatusService,
    private toastr: ToastrService,
    private insights: Insights,
    public dialog: MatDialog,
    private lookupService: LookupDataService,
    private common: CommonFunctionService) {
      this.vesselOptions = linkedSignal(() => dataSync.current_user().operatedVessels);

      //Completed delivery or not, passed via URL
      this.viewMode = +this.route.snapshot.paramMap.get('pending')!;

      if (this.viewMode == DeliveryMode.Current) {
        this.updateEffect = effect(() => {
          this.landing = this.dataSync.user_trip().hmsDelivery;
          this.landing.sets = this.dataSync.user_trip().sets.filter(s => s.fishery == 'HMS');
          this.patchForm(false);
          this.sortSetsByDate();
          this.table?.renderRows();
        });
      } else if (this.viewMode == DeliveryMode.EditCompleted) {
        this.updateEffect = effect(() => {
          this.landing = this.dataSync.edited_hms();
          this.patchForm(false);
          this.sortSetsByDate();
          this.table?.renderRows();
        })
      } else if (this.viewMode == DeliveryMode.Draft) {
        this.updateEffect = effect(() => {
          let draft = this.dataSync.hms_drafts().find(x => x.localLandingGUID == this.landing.localLandingGUID);
          if (!draft) this.router.navigate(['hms_drafts']);
          else {
            this.landing = draft;
            this.patchForm(false);
            this.sortSetsByDate();
            this.table?.renderRows();
          }       
      });
    }
  }


  ngOnInit(): void {
    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.previousSetIndex = +(this.route.snapshot.queryParams.setIndex ?? 0);
    this.matToolTipText = this.previousScreen == 'set_detail_hms' ? 'Back to Set' : this.viewMode != 0 ? 'Back to Deliveries' : '';

    this.completedInd = this.viewMode != DeliveryMode.Current ? Number.parseInt(<string>this.route.snapshot.paramMap.get('index')) : -1;
    switch (this.viewMode) {
      case DeliveryMode.Current:
        this.landing = this.dataSync.user_trip().hmsDelivery;
        this.landing.sets = this.dataSync.user_trip().sets.filter(s => s.fishery == 'HMS');
        if (this.landing.isNewDelivery) {
          console.log("New delivery");
          let last = this.dataSync.received_logbooks().find(lb => lb instanceof HMSDelivery) as HMSDelivery;
          if (last) this.landing.vessel = VesselData.transformAnonymous([last.vessel])[0];
            
          this.landing.isNewDelivery = false;
          this.landing.operatorName = `${this.dataSync.current_user().firstName} ${this.dataSync.current_user().lastName}`;
          this.minArrivalDate = this.dataSync.user_trip().sets.filter(st => st.fishery == 'HMS' && st.startDate)
          .map(fe => fe.startDate).reduce((curr, acc) => new Date(curr).getTime() > new Date(acc).getTime() ? curr : acc, new Date());      

          this.dataSync.user_trip.update(trip => {
            trip.hmsDelivery = this.landing;
            return trip; 
          });
          // Set departure date to the earliest date from all hms sets in this trip
          this.dataSync.updateLanding(Fishery.HMS, false).then(result => console.log("Delivery " + (result ? "updated" : "failed")));
        }
        break;
        case DeliveryMode.Draft:
          this.landing = this.dataSync.hms_drafts()[this.completedInd];
          break;
      case DeliveryMode.Completed:
        this.landing = this.dataSync.received_logbooks()[this.completedInd] as HMSDelivery;
        this.canEdit = new Date(this.landing.receivedDate.valueOf() + 7 * 24 * 60 * 60 * 1000) > new Date();
        this.vesselOptions.set(VesselData.transformAnonymous([this.landing.vessel]));
        this.landingForm.disable();
        break;
      case DeliveryMode.EditCompleted: //Edited to be implemented later
        this.landing = this.dataSync.edited_hms();  
        if (new Date(this.landing.receivedDate.valueOf() + 7*24*60*60*1000) < new Date()) {
          this.landing = this.dataSync.received_logbooks()[this.completedInd] as HMSDelivery;
          this.discardEdits(false);
          this.toastr.warning('One week edit time window for this delivery has passed. Contact FedElog@psmfc.org if you have any questions', 'Logbook edits removed', {timeOut: 8000});            
        } else this.canEdit = true;
        break;
    }

    // Set minimum valid date for arrival date
    // Initialize minArrivalDate with departureDate
    let allDates = this.dataSync.user_trip().sets.filter(st => st.fishery == 'HMS' && st.startDate).map(s => new Date(s.startDate)).filter(d => d && !Number.isNaN(d.getTime()));
    this.minArrivalDate = allDates.reduce((curr, acc) => new Date(curr).getTime() > new Date(acc).getTime() ? curr : acc, new Date(this.landing.departureDate ?? 0));
    // At this point, minArrivalDate should contain the maximum startDate found
    console.log('Max Arrival Date:', this.minArrivalDate);

    this.patchForm();

     if (this.viewMode != DeliveryMode.Completed) { 
      this.subscribeToFormUpdate();
      this.resetErrorContent();
      this.portOptions = this.lookupService.getHmsPorts();
      this.filteredDeparturePorts = this.landingForm.get('departurePort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
      this.filteredArrivalPorts = this.landingForm.get('arrivalPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
    }

    this.sortSetsByDate();
  }


  patchForm(emitEvent: boolean = true) {
    this.landingForm.patchValue({
      vessel: this.landing.vessel, //Default to last vessel entered
      operator: this.landing.operatorName, //Default to account name
      departureDate: this.landing.departureDate ? formatDate(this.landing.departureDate, 'fullDate', 'en-US') : '', //Default to earliest set date
      departurePort: this.landing.departurePort.name ? this.landing.departurePort : '',
      arrivalPort: this.landing.arrivalPort.name ? this.landing.arrivalPort : '', //Default to last port entered
      arrivalDate: this.landing.arrivalDate ? formatDate(this.landing.arrivalDate, 'fullDate', 'en-US') : '',
    }, {emitEvent: emitEvent});

  }


  subscribeToFormUpdate() {
    this.landingFormSubscription = this.landingForm.valueChanges.subscribe(value => {
      console.log("Update delivery")
      if (!value.vessel) {
        value.vessel = this.landing.vessel;
        this.landingForm.get('vessel')!.setValue(value.vessel, { onlySelf: true, emitEvent: false });
      }

      (this.landing as HMSDelivery).updateValue(value);
      this.dataSync.updateLanding(Fishery.HMS, this.viewMode == DeliveryMode.EditCompleted, 'default', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
        if (res.success) this.resetErrorContent();
        else {
          this.toastr.error('Error updating delivery', 'Error', { timeOut: 5000 });
          this.insights.trackException(res.error);
        } 

      });

    });

  }


  /**
  * Sort the sets by date first then by their set numbers.
  */
  sortSetsByDate(): void {

    var compareSetNumbers = (a: string, b: string) => { return !b ? -1 : !a ? 1 : a.localeCompare(b); }
    var compareSetDates = (aString: string, bString: string, aNumber: string, bNumber: string) => {
      return (aString < bString) ? -1 : (aString > bString) ? 1 : compareSetNumbers(aNumber, bNumber);
    }

    this.landing.sets.sort((a, b) => {

      // start sorting, check if dates are null first
      return (a.startDate && !b.startDate) ? -1 :
        (!a.startDate && b.startDate) ? 1 :
          (!a.startDate && !b.startDate) ? compareSetNumbers(a.setNumber, b.setNumber) :
            compareSetDates(new Date(a.startDate).toISOString(), new Date(b.startDate).toISOString(), a.setNumber, b.setNumber);
    });

    this.sortedSets = this.landing.sets;
  }


  navigateBack() {
    if (this.previousScreen == 'set_detail_hms') this.router.navigate(['set_detail_hms', this.previousSetIndex, false]);
    else this.router.navigate([this.previousScreen, { queryParams: { fishery: 1 } }]);
  }


  compareFn(l1: LookupItem, l2: LookupItem): boolean {
    return l1 && l2 ? l1.code === l2.code : l1 === l2;
  }

  compareVessel(v1: VesselData, v2: VesselData): boolean {
    return v1 && v2 ? v1.vesselName + v1.vesselCoastNumber === v2.vesselName + v2.vesselCoastNumber : v1 === v2;
  }


  /**
   * As in settings we can add a new vessel here.
   */
  addVessel() {
    this.common.addVessel(-1, false).then((newVessel: VesselData) => {
      this.landingForm.get('vessel')?.setValue(newVessel);
    }).catch((err: any) => console.log(err))
  }


  /**
   * Go get all errors/warnings for the delivery and its sets
   */
  resetErrorContent() {
    this.errors = this.tripValidator.errorWarningDialogContentHMS(this.viewMode == DeliveryMode.EditCompleted, this.viewMode == DeliveryMode.Draft ? this.completedInd : -1);
    this.onlyWarnings = !this.errors.some(x => x.isError);
    this.validSets.clear();
    this.preExistingSets.clear();
    let allSets = this.dataSync.user_trip().sets;
    this.landing.sets.forEach(set => {
      this.validSets.set(set.fishingEventID, this.errors.find(x => x.setID == set.fishingEventID && x.isError) == undefined ? true : false);
      this.preExistingSets.set(set.fishingEventID, allSets.findIndex((fe: FishingEvent) => fe.fishingEventID == set.fishingEventID) == -1);
    });}


  /**
 * Display errors/warnings to the user if there are any. Otherwise we go to the summary-review component.
 */
  openSubmitDialog() {
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this.dialog.open(SubmitDialogComponent, {
        autoFocus: false,
        width: '400px',
        height: '450px',
        data: this.errors.filter(x => x.isError).length > 0
      });

    } else {
      this.openErrorDialog(true);
    }
  }


  openUnloadingDialog(index: number) {
    if (this.viewMode == DeliveryMode.Completed) return;
    let isNew: boolean = false;
    if (index >= this.landing.unloading.length) {
      this.landing.unloading.push(new Unloading());
      this.landing.unloading[index].unloadingDate = this.landingForm.get('arrivalDate')?.value == '' ? new Date() : new Date(this.landingForm.get('arrivalDate')?.value);
      this.landing.unloading[index].unloadingPort = this.landingForm.get('arrivalPort')?.value == '' ? '' : this.landingForm.get('arrivalPort')?.value;
      isNew = true;
    }

    this.dialog.open(AddUnloadingDialogComponent, {
      width: SCREEN_WIDTH,
      data: { unloading: this.landing.unloading[index], isNew: isNew },
      autoFocus: false
    }).afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.delete) {
          this.landing.unloading.splice(index, 1);
        } else {
          this.landing.unloading[index].unloadingPort = result.unloadingPort;
          this.landing.unloading[index].unloadingDate = result.unloadingDate;
          this.landing.unloading[index].species = result.species;
        }
        this.table.renderRows();
        this.dataSync.updateLanding(Fishery.HMS, this.viewMode == DeliveryMode.EditCompleted, 'default', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
          if (!res.success) {
            this.toastr.error('Error adding unloading', 'Error', { timeOut: 5000 });
            this.insights.trackException(res.error);
          } 
  
        });
      } else if (isNew) this.landing.unloading.splice(index, 1);
      this.resetErrorContent();
    });
  }

  removeSet(index: number) {
    //Editing?
    this.landing.sets.splice(index, 1);
    this.dataSync.user_trip.update(trip => {
      trip.hmsDelivery = this.landing;
      return trip;
    })
    this.dataSync.updateLanding(Fishery.HMS, this.viewMode == DeliveryMode.EditCompleted, 'default', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
      if (res.success) this.resetErrorContent();
      else {
        this.toastr.error('Error updating delivery', 'Error', { timeOut: 5000 });
        this.insights.trackException(res.error);
      } 

    });
  }


  openErrorDialog(submission: boolean = false) {
    this.resetErrorContent();

    if (this.errors.length == 0 && submission) {
      console.log('Going to summary review')
      this.router.navigate(['summary_review', 'HMS'], { queryParams: { previous: this.previousScreen, 
        deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1, draftIndex: this.viewMode == DeliveryMode.Draft ? this.completedInd : -1 } });
      return;
    }

    //Launch dialog with collected results
    const dialogRef = this.dialog.open(ErrorWarningDialogComponent, {
      autoFocus: false,
      width: (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) - 10).toString() + 'px',
      height: (Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - 10).toString() + 'px',
      data: { errors: this.errors, submission: submission }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != undefined) {
        if (submission && result == -2) {
          //Go to summary review
          console.log('Going to summary review')
          this.router.navigate(['summary_review', 'HMS'], { queryParams: { previous: this.previousScreen, 
            deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1, draftIndex: this.viewMode == DeliveryMode.Draft ? this.completedInd : -1 } });
        } else if (result != -1) {
          if (this.viewMode == DeliveryMode.EditCompleted)
            this.router.navigate(['set_detail_hms', -1, false], { queryParams: { editedIndex: result } });
          else
            this.router.navigate(['set_detail_hms', result, false], { queryParams: { previous: 'delivery' } });
          
          if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
        }
      }
    });
  }


  editDateTime(from: string) {
    if (this.viewMode == DeliveryMode.Completed) return;
    let dateInput = this.landingForm.get(from)?.value == '' ? new Date() : new Date(this.landingForm.get(from)?.value);

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      data: { date: dateInput, minDate: this.minArrivalDate, isDelivery: true, hmsField: from },
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) this.landingForm.get(from)?.setValue(formatDate(result, 'fullDate', 'en-US'));
    });
  }

    /**
     * For adding/removing sets attached to the delivery
     * @returns 
     */
    openSetSelection() {
      //Editing?
      if (this.viewMode == DeliveryMode.Completed) return;
      if (this.viewMode === DeliveryMode.Draft) {
        // Sets already in the draft landing.
        const draftSetIDs: string[] = this.landing.sets.map(s => s.fishingEventID);
        // Sets from the active trip.
        const currentTripSets: FishingEvent[] = this.dataSync.user_trip().sets;
        // Create a union: include all sets from both sources.
        const unionMap = new Map<string, FishingEvent>();
        currentTripSets.forEach(s => unionMap.set(s.fishingEventID, s));
        this.landing.sets.forEach(s => unionMap.set(s.fishingEventID, s));

        const originalHMSUnion: FishingEvent[] = Array.from(unionMap.values());
        
        const dialogRef = this.dialog.open(SetSelectionDialogComponent, {
          width: '400px',
          height: '600px',
          data: {
            data: draftSetIDs,
            combinedHMSSets: originalHMSUnion,
            fishery: 'HMS',
            editedDelivery: false,
            isHMSDraft: true
          }
        });
        
        dialogRef.afterClosed().subscribe((result: FishingEvent[]) => {
          if (result) {
            // Update the draft landing's sets with the selected result.
            this.landing.sets = result;
            // First update the draft landing
            this.dataSync.updateLanding(Fishery.HMS, false, 'default', this.landing.localLandingGUID, true).then(res => {
              if (res.success) {
                // Calculate updated active trip sets: original union minus the draft sets.
                const newDraftIDs = new Set(this.landing.sets.map(s => s.fishingEventID));
                this.dataSync.user_trip.update(trip => {
                  trip.sets = originalHMSUnion.filter((set: FishingEvent) => !newDraftIDs.has(set.fishingEventID));
                  return trip;
                });
                // Save active trip updates by calling updateTrip.
                this.dataSync.updateTrip('default').then(res => {
                  if (res.success) {
                    this.resetErrorContent();
                    this.sortSetsByDate();
                  } else {
                    this.toastr.error('Error updating trip', 'Error', { timeOut: 5000 });
                    this.insights.trackException(res.error);
                  }
                });
              } else {
                this.toastr.error('Error updating draft delivery', 'Error', { timeOut: 5000 });
                this.insights.trackException(res.error);
              }
            });
          }
        });
      } else {
        // Non-draft behavior.
        let ids: string[] = this.landing.sets.map(s => s.fishingEventID);
        const dialogRef = this.dialog.open(SetSelectionDialogComponent, {
          width: '400px',
          height: '600px',
          data: {
            data: ids,
            editedDelivery: this.viewMode == DeliveryMode.EditCompleted,
            fishery: 'HMS'
          }
        });
    
        dialogRef.afterClosed().subscribe((result: FishingEvent[]) => {
          if (result) {
            this.landing.sets = this.viewMode == DeliveryMode.EditCompleted ?
              this.landing.sets.filter((fe: FishingEvent) => this.preExistingSets.get(fe.fishingEventID)).concat(result) : result;
            this.dataSync.updateLanding(Fishery.HMS, this.viewMode == DeliveryMode.EditCompleted).then(res => {
              if (res.success) {
                this.resetErrorContent();
                this.sortSetsByDate();
              } else {
                this.toastr.error('Error updating delivery', 'Error', { timeOut: 5000 });
                this.insights.trackException(res.error);
              }            
            });
          }
        });
      }
  }
  

  /**
   * Clicking on a set attached to delivery takes us to that set
   * @param setID fishingEventID of set to go to
   * @returns 
   */
  goToSet(setID: string) {
    //Editing?  
  // When the delivery is completed, go to the set page in a read-only mode.
    if (this.viewMode == DeliveryMode.Completed) {
      // Find the set index from the landing sets.
      const setIndex = this.landing.sets.findIndex(x => x.fishingEventID === setID);
      if (setIndex !== -1) {
        // Navigate to the set page and pass a parameter that indicates "readOnly" mode.
        this.router.navigate(['set_detail_hms', setIndex, false], { queryParams: { previous: 'deliveries_list', completedIndex: this.completedInd, readOnly: true } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) {
          window.scrollTo(0, 0);
        }
      }
    } else {
      let setIndex = this.dataSync.user_trip().sets.findIndex(x => x.fishingEventID === setID);
      if (setIndex != -1) {
        this.router.navigate(['set_detail_hms', setIndex, false], { queryParams: { previous: 'delivery' } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
      } else if (this.viewMode == DeliveryMode.EditCompleted) {
        //The set no longer exists on device and is saved as part of this delivery rather than the user sets
        this.router.navigate(['set_detail_hms', -1, false], { queryParams: { editedIndex: this.landing.sets.findIndex(x => x.fishingEventID === setID) } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
      } else if (this.viewMode == DeliveryMode.Draft) {
        this.router.navigate(['set_detail_hms', -1, false], { queryParams: { draftIndex: this.completedInd, draftSetId: setID } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
      }
    }
  }


  enableEdits() {
    let logbook = this.dataSync.edited_groundfish().logbookId ? this.dataSync.edited_groundfish() : this.dataSync.edited_hms();
    if (logbook.logbookId && logbook.logbookId != this.landing.logbookId) {
      this.toastr.warning(`Logbook ${logbook.logbookId.substring(17)} has unsubmitted edits. Submit or discard those to edit this logbook`, 
      'Multiple edited logbooks');
      return;
    }

    this.dataSync.editExistingHMSLanding(this.landing.logbookId).then(res => {
      if (!res.success) {
        this.toastr.error('Error editing delivery', 'Error', { timeOut: 5000 });
        this.insights.trackException(res.error);
        return;
      }
      
      this.viewMode = DeliveryMode.EditCompleted;
      this.router.navigate(['hms_delivery_detail', 2, this.completedInd], { queryParams: { previous: 'deliveries_list' } });
      this.landing = this.dataSync.edited_hms();
      
      this.landingForm.enable();

      this.subscribeToFormUpdate();
      this.resetErrorContent();
      this.portOptions = this.lookupService.getHmsPorts();
      this.filteredDeparturePorts = this.landingForm.get('departurePort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
      this.filteredArrivalPorts = this.landingForm.get('arrivalPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
    });

    this.updateEffect = effect(() => {
        this.landing = this.dataSync.edited_hms();
        this.patchForm();
        this.sortSetsByDate();
        this.table.renderRows();
      }, {injector: this.injector});
  }


  discardEdits(promptUser: boolean) {
    if (this.updateEffect) this.updateEffect.destroy();
    let discard = () => {
      this.viewMode = DeliveryMode.Completed;

      this.landingSubscription?.unsubscribe(); 
      this.landingFormSubscription?.unsubscribe();
      this.landingForm.disable();

      this.dataSync.discardLogbookEdits();

      //Unassign LocalStorageService.edited_landing, revert this landing to completed delivery landing
      this.landing = this.dataSync.received_logbooks()[this.completedInd] as HMSDelivery;
      this.vesselOptions.set(VesselData.transformAnonymous([this.dataSync.received_logbooks()[this.completedInd].vessel]));
      this.landingForm.patchValue({
        vessel: this.dataSync.received_logbooks()[this.completedInd].vessel, //Default to last vessel entered
        operator: this.dataSync.received_logbooks()[this.completedInd].operatorName, //Default to account name
        departureDate: this.landing.departureDate ? formatDate(this.landing.departureDate, 'fullDate', 'en-US') : '', //Default to earliest set date
        departurePort: this.landing.departurePort.name ? this.landing.departurePort : '',
        arrivalPort: this.landing.arrivalPort.name ? this.landing.arrivalPort : '', //Default to last port entered
        arrivalDate: this.landing.arrivalDate ? formatDate(this.landing.arrivalDate, 'fullDate', 'en-US') : '',
      });

      this.sortSetsByDate();
    };

    if (promptUser) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {screen: 'discardedits'}
      }).afterClosed().subscribe(result => {
        if (!result) return;
        discard();  
      });  
    } else discard();
  }

  ngOnDestroy() {
    if (this.viewMode != DeliveryMode.Completed) {
      if (this.landingFormSubscription) this.landingFormSubscription.unsubscribe();
      if (this.portSubscription) this.portSubscription.unsubscribe();
      if (this.landingSubscription) this.landingSubscription.unsubscribe();
    }
  }

}
